import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import isEmpty from 'is-empty';
import {Box, Typography} from '@mui/material';

import {Dispatch, Store} from 'spectra-logic-ui';
import {fetchResource} from 'spectra-logic-ui/actions';
import {Table} from 'spectra-logic-ui/components';

import {Action, UserAction} from '@/types';
import {IAMUser} from '@/iam/types';
import ButtonToolbar from '@/components/button_toolbar';
import AssignActionDialog from '@/iam/users/details/form/assign_action';
import RemoveActionDialog from '@/iam/users/details/form/remove_action';

type Props = {
  actions: Action[];
  user: IAMUser;
  userActions?: UserAction[];
  fetchUserActions?: () => Promise<any>;
  error?: boolean;
  fetching?: boolean;
}

const Actions = ({actions=[], user, userActions=[], fetchUserActions, error, fetching}: Props) => {
  const [selectedUserActionId, setSelectedUserActionId] = useState('');

  useEffect(() => {
    if (fetchUserActions) {
      fetchUserActions();
    }
  }, []);

  const actionsMap: Record<string, Action> = {};
  actions.forEach((action: Action) => {
    actionsMap[action.id] = action;
  });

  // Default to the first action if none are selected
  // and there's only one user action.
  let userActionIdToUse = selectedUserActionId;
  if (!userActionIdToUse && userActions.length === 1) {
    userActionIdToUse = userActions[0].id;
  }
  const userActionToUse = userActions.find((ua: UserAction) => ua.id === userActionIdToUse);

  return (
    <div>
      <ButtonToolbar>
        <ButtonToolbar.CreateDialogButton title='Assign' onSuccess={fetchUserActions}
          dialog={(props: any) => (
            <AssignActionDialog user={user} userAction={userActionToUse} actions={actions} {...props} />
          )}
        />
        <ButtonToolbar.DeleteDialogButton title='Remove' disabled={isEmpty(userActionIdToUse)}
          onSuccess={fetchUserActions}
          dialog={(props: any) => (
            <RemoveActionDialog user={user} userAction={userActionToUse} actions={actions} {...props} />
          )}
        />
      </ButtonToolbar>
      <Table>
        <Table.Body isLoading={fetching} hasError={error}>
          {(userActions.map((ua) => (
            <Table.Row key={ua.action}
              selected={selectedUserActionId === ua.id}
              onClick={() => setSelectedUserActionId(ua.id)}
            >
              <Table.Cell><ActionSummary action={actionsMap[ua.id]} userAction={ua} /></Table.Cell>
            </Table.Row>
          )))}
        </Table.Body>
      </Table>
    </div>
  );
};

export const ActionSummary = ({action, userAction}: {action?: Action; userAction?: UserAction}) => {
  if (!userAction) {
    return null;
  }
  if (!action) {
    return <Box>{userAction.id}</Box>;
  }

  let title = action.title;
  if (action.version) {
    title += ` - Version ${action.version}`;
  }

  return (
    <>
      <Typography sx={{fontWeight: 'bold'}}>{title}</Typography>
      <Typography>{action.description}</Typography>
      {userAction.args && userAction.args.length > 0 &&
        <Typography>Arguments: <i>{userAction.args.join(' ')}</i></Typography>}
    </>
  );
};

const mapStateToProps = (state: Store, {user}: Props) => {
  const resource = `actions/${user.accountid}/all/${user.username}`;
  const userActionsResource = state.resources[resource] || {};
  return {
    userActions: userActionsResource.data,
    error: userActionsResource.error,
    fetching: userActionsResource.fetching,
  };
};

const mapDispatchToProps = (dispatch: Dispatch, {user}: Props) => {
  const resource = `actions/${user.accountid}/all/${user.username}`;
  return {
    fetchUserActions: () => dispatch(fetchResource(resource)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Actions);
